import React from 'react'

/**
 * Estrada de neon — o grafismo central da identidade "Nauta": a via serpenteia
 * pelo escuro com brilho azul nas bordas, centro branco tracejado e pins de
 * mapa nas paradas.
 *
 * Substitui o `GraficoRota` (linha tracejada simples) mantendo a MESMA API
 * (`className`), então os usos antigos trocam só o import.
 *
 * GLOW POR STROKES SOBREPOSTOS, não `feGaussianBlur`: são três traçados do
 * mesmo path — um largo e translúcido (halo), um médio (corpo) e o centro
 * branco tracejado. Filtro de blur num elemento deste tamanho é raster em
 * tempo real a cada frame/scroll e custa FPS em celular; strokes são
 * geometria, o compositor resolve de graça.
 *
 * `vector-effect: non-scaling-stroke` (no CSS) em todos os traços: o SVG é
 * usado em escalas muito diferentes (do canto de um card ao hero inteiro) e,
 * sem isso, o tracejado do centro vira uma barra sólida quando ampliado.
 */

/* Âncoras da curva. Os pins ficam EXATAMENTE sobre estes pontos porque são os
   nós inicial/final dos dois beziers — mover a curva sem mover os pins deixa
   marcador flutuando fora da pista (foi o erro clássico do grafismo antigo,
   documentado lá). */
const INICIO = [18, 150]
const MEIO = [94, 90]
const FIM = [176, 26]
const CAMINHO = `M${INICIO[0]} ${INICIO[1]} C 58 150 54 100 ${MEIO[0]} ${MEIO[1]} C 134 80 130 38 ${FIM[0]} ${FIM[1]}`

/* VARIANTE "CONECTOR" (13/08) — a mesma via, em formato de TRECHO LONGO.
 *
 * Por que uma segunda geometria em vez de esticar a primeira: o traçado padrão
 * é 190×160 (1,19:1) e o SVG não declara `preserveAspectRatio`, então vale
 * `xMidYMid meet` — numa caixa larga a curva encolhe até caber na ALTURA e
 * fica centrada, com ar dos dois lados. Foi o que aconteceu entre as mesas do
 * Apre.Lab: um conector de 152px boiando num vão de 774px, sem encostar em
 * nenhum dos dois cartões (medido a 1650px em 13/08). Esticar por
 * `preserveAspectRatio="none"` resolveria a largura e achataria os dois pins
 * de mapa em elipses.
 *
 * Então a caixa não cede: cede o DESENHO. Aqui a via atravessa um viewBox de
 * 520×120 (4,3:1) descendo da quina superior esquerda à inferior direita, que
 * é exatamente o percurso de uma mesa à seguinte. Quem consome precisa casar a
 * proporção da caixa com esta — ver `aspect-ratio` em .aprelab-caminho. */
const CONECTOR_MEIO = [260, 60]
const CONECTOR_FIM = [510, 106]
const CONECTOR = `M10 14 C 150 14 130 60 ${CONECTOR_MEIO[0]} ${CONECTOR_MEIO[1]} C 390 60 370 106 ${CONECTOR_FIM[0]} ${CONECTOR_FIM[1]}`

/** Pin de mapa (gota) com o miolo vazado, centrado em (cx, cy) na ponta. */
function Pin({ cx, cy, escala = 1 }) {
  return (
    <g transform={`translate(${cx} ${cy}) scale(${escala})`} className="estrada-pin">
      <path d="M0 0 C0 0 -9 -10 -9 -16 A9 9 0 1 1 9 -16 C9 -10 0 0 0 0 Z" />
      <circle cx="0" cy="-16" r="3.4" className="estrada-pin-miolo" />
    </g>
  )
}

export default function EstradaNeon({ className, variante }) {
  const conector = variante === 'conector'
  const d = conector ? CONECTOR : CAMINHO
  const meio = conector ? CONECTOR_MEIO : MEIO
  const fim = conector ? CONECTOR_FIM : FIM

  return (
    <svg
      className={className}
      viewBox={conector ? '0 0 520 120' : '0 0 190 160'}
      aria-hidden="true"
      focusable="false"
    >
      <path className="estrada-halo" d={d} />
      <path className="estrada-corpo" d={d} />
      <path className="estrada-centro" d={d} />
      <Pin cx={meio[0]} cy={meio[1]} escala={0.7} />
      <Pin cx={fim[0]} cy={fim[1]} escala={0.85} />
    </svg>
  )
}
